import { Box, Button, CircularProgress, LinearProgress, Skeleton, Typography } from "@mui/material";
import { ErrorOutlineRounded, InboxOutlined, RefreshRounded } from "@mui/icons-material";
import { brand, fontSize, neutral } from "app/theme/tokens";

/**
 * Loading indicator.
 * variant: "spinner" (default, centred) | "bar" (thin top progress) | "skeleton" (n table-like rows)
 */
export function LoadingState({ variant = "spinner", label, rows = 6, fullPage = false, size = 28, sx }) {
  if (variant === "bar") return <LinearProgress sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 2, ...sx }} />;

  if (variant === "skeleton") {
    return (
      <Box sx={{ p: 1.5, ...sx }}>
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} variant="rounded" height={22} sx={{ mb: 1, opacity: 1 - i * (0.6 / rows) }} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      role="status"
      sx={{
        width: "100%",
        height: fullPage ? "100%" : "auto",
        minHeight: fullPage ? 240 : 120,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.25,
        ...sx,
      }}
    >
      <CircularProgress size={size} thickness={4} sx={{ color: brand.tealDark }} />
      {label && <Typography sx={{ fontSize: fontSize.sm, color: neutral[500] }}>{label}</Typography>}
    </Box>
  );
}

/** Placeholder shown when a list/table/search returns nothing. */
export function EmptyState({ title = "No data", message, icon: Icon = InboxOutlined, action, compact = false, sx }) {
  return (
    <Box sx={{ textAlign: "center", py: compact ? 2 : 5, px: 2, color: neutral[500], ...sx }}>
      <Icon sx={{ fontSize: compact ? 28 : 40, color: neutral[300], mb: 0.5 }} />
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, color: neutral[700] }}>{title}</Typography>
      {message && <Typography sx={{ fontSize: fontSize.sm, mt: 0.5 }}>{message}</Typography>}
      {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
    </Box>
  );
}

/** Error block with optional retry. */
export function ErrorState({ title = "Something went wrong", message, onRetry, compact = false, sx }) {
  return (
    <Box sx={{ textAlign: "center", py: compact ? 2 : 5, px: 2, ...sx }}>
      <ErrorOutlineRounded sx={{ fontSize: compact ? 28 : 40, color: "error.main", mb: 0.5 }} />
      <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, color: neutral[800] }}>{title}</Typography>
      {message && <Typography sx={{ fontSize: fontSize.sm, color: neutral[500], mt: 0.5 }}>{message}</Typography>}
      {onRetry && (
        <Button size="small" variant="outlined" startIcon={<RefreshRounded />} onClick={onRetry} sx={{ mt: 1.5 }}>
          Retry
        </Button>
      )}
    </Box>
  );
}
